import {
  ALL_LAYOUT_ARGS,
  CONTAINER_ARGS,
  FLEX_ARGS,
  GRID_ARGS,
  POSITIONING_ARGS,
  getArgumentNamesFromMetadata,
  getDefaultValueFromMetadata,
} from '../../../utils/spds/metadata';
import { BOOLEAN, STRING } from '../../../utils/component-validations';
import { bool, or } from 'macro-decorators';
import { element } from 'ember-element-helper';
import { htmlSafe } from '@ember/template';
import { modifier } from 'ember-modifier';
import { service } from '@ember/service';
import { verifyHtmlTag } from '../../../utils/spds/html-verification';
import Super from './base.gjs';
import styles from './container.module.scss';

const METADATA = {
  component: 'Spds::Core::Container',
  description: `
    Generic container component

    ### Customization

    By default, the rendered HTML element is a \`div\` but any valid HTML element can be rendered by providing the \`@tag\` argument.

    ### Styling

    The default CSS \`display\` for the container is \`flex\` with a \`flex-direction\` of \`row\`. It can be changed to \`inline-flex\` by
    providing the \`@inline\` argument. The \`flex-direction\` can be changed to \`column\` by providing the \`@column\` argument.
    Additional styling can be added to the container by assigning custom CSS classes to the component through the HTML \`class\` attribute
    as normal.

    Similarly, there are some arguments which can be used to quickly apply height and width sizing to restrict the size of the container:

    * \`@fitContent\`: this restricts both the height and width of the container to fit its content
    * \`@fitContentHeight\`: adds the size restriction to just the height of the container
    * \`@fitContentWidth\`: adds the size restriction to just the width of the container

    ### Positioning

    The \`@absolute\` and \`@fixed\` arguments will apply CSS classes that set the container's positioning to those respective values.
    Specific X and Y positioning can be applied by adding a custom CSS class that provides that data.

    ### Discarding clicks

    In the case that the content of the container should not be clickable for some reason, there is a preconfigured modifier which can be applied
    to the container automatically by passing the \`@discardClick\` argument.
  `,
  meta: {
    ...CONTAINER_ARGS.meta,
    ...POSITIONING_ARGS.meta,
    ...ALL_LAYOUT_ARGS.meta,
    content: {
      defaultValue: '',
      description: 'HTML content to render inside the container',
      type: STRING,
    },
    discardClick: {
      defaultValue: false,
      description: 'Prevent click events from propagating',
      type: BOOLEAN,
    },
    error: {
      defaultValue: false,
      description: 'Apply error styling',
      type: BOOLEAN,
    },
    inline: {
      defaultValue: false,
      description: 'Display container inline',
      type: BOOLEAN,
    },
    inputContainer: {
      defaultValue: false,
      description: 'Apply input container styling',
      type: BOOLEAN,
    },
    subgrid: {
      defaultValue: false,
      description: 'Use CSS subgrid',
      type: BOOLEAN,
    },
    tag: {
      defaultValue: 'div',
      description: 'HTML tag to use for the container element',
      type: STRING,
    },
  },
  name: 'Container',
  resources: {
    github:
      'https://github.com/simplepractice/ember-simplepractice/tree/staging/addon/src/components/spds/core/container.gjs',
  },
};

export default class SpdsCoreContainer extends Super {
  static metadata = this.setMetadata(METADATA);

  baseClass = 'spds-container';
  classesToMapToArgs = ['absolute', 'error', 'fixed', 'inline', 'inputContainer', 'subgrid'];
  classesToMapToAttrs = ['column', 'fitContentHeight', 'fitContentWidth', 'grid', 'reverse'];
  styles = styles;

  @service fastboot;

  @bool('args.fitContent') fitContent;
  @bool('args.fitContentHeight') fitContentH;
  @bool('args.fitContentWidth') fitContentW;
  @bool('args.discardClick') shouldDiscardClick;

  @or('fitContent', 'fitContentH') fitContentHeight;
  @or('fitContent', 'fitContentW') fitContentWidth;

  get propsToMapToAttrs() {
    return [...getArgumentNamesFromMetadata(this.grid ? GRID_ARGS : FLEX_ARGS)];
  }

  get alignContent() {
    return (
      this.args.alignContent ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'alignContent')
    );
  }

  get alignItems() {
    return (
      this.args.alignItems ?? getDefaultValueFromMetadata(this.constructor.metadata, 'alignItems')
    );
  }

  get alignSelf() {
    return (
      this.args.alignSelf ?? getDefaultValueFromMetadata(this.constructor.metadata, 'alignSelf')
    );
  }

  get column() {
    return this.args.column && !this.grid;
  }

  get content() {
    return htmlSafe(this.args.content);
  }

  get defaultGap() {
    return getDefaultValueFromMetadata(this.constructor.metadata, 'gap');
  }

  get flex() {
    return this.args.flex ?? getDefaultValueFromMetadata(this.constructor.metadata, 'flex');
  }

  get flexWrap() {
    return this.args.flexWrap ?? getDefaultValueFromMetadata(this.constructor.metadata, 'flexWrap');
  }

  get gap() {
    return this.args.gap ?? getDefaultValueFromMetadata(this.constructor.metadata, 'gap');
  }

  get grid() {
    return this.args.grid ?? getDefaultValueFromMetadata(this.constructor.metadata, 'grid');
  }

  get gridArea() {
    return (
      this.args.gridArea ??
      this.args.area ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'gridArea')
    );
  }

  get gridAutoColumns() {
    return (
      this.args.gridAutoColumns ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'gridAutoColumns')
    );
  }

  get gridAutoFlow() {
    return (
      this.args.gridAutoFlow ??
      this.args.autoFlow ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'gridAutoFlow')
    );
  }

  get gridAutoRows() {
    return (
      this.args.gridAutoRows ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'gridAutoRows')
    );
  }

  get gridColumn() {
    return (
      this.args.gridColumn ?? getDefaultValueFromMetadata(this.constructor.metadata, 'gridColumn')
    );
  }

  get gridRow() {
    return (
      this.args.gridRow ??
      this.args.row ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'gridRow')
    );
  }

  get gridTemplateAreas() {
    return (
      this.args.gridTemplateAreas ??
      this.args.areas ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'gridTemplateAreas')
    );
  }

  get gridTemplateColumns() {
    return (
      this.args.gridTemplateColumns ??
      this.args.columns ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'gridTemplateColumns')
    );
  }

  get gridTemplateRows() {
    return (
      this.args.gridTemplateRows ??
      this.args.rows ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'gridTemplateRows')
    );
  }

  get justifyContent() {
    return (
      this.args.justifyContent ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'justifyContent')
    );
  }

  get justifyItems() {
    return (
      this.args.justifyItems ??
      getDefaultValueFromMetadata(this.constructor.metadata, 'justifyItems')
    );
  }

  get justifySelf() {
    return (
      this.args.justifySelf ?? getDefaultValueFromMetadata(this.constructor.metadata, 'justifySelf')
    );
  }

  get order() {
    return this.args.order ?? getDefaultValueFromMetadata(this.constructor.metadata, 'order');
  }

  get reverse() {
    return this.args.reverse && !this.grid;
  }

  get tag() {
    let ELEMENT_TAG_DEFAULT = getDefaultValueFromMetadata(this.constructor.metadata, 'tag');
    let { tag = ELEMENT_TAG_DEFAULT } = this.args;

    if (this.fastboot?.isFastBoot) return tag;

    return verifyHtmlTag(ELEMENT_TAG_DEFAULT, tag);
  }

  setupDiscardClick = modifier(() => {
    if (this.clickHandlerSetup || !this.shouldDiscardClick || !this.baseElement) return;

    this.clickHandlerSetup = true;

    this.baseElement.addEventListener('click', this.ignoreClick);

    return () => {
      this.baseElement.removeEventListener('click', this.ignoreClick);
    };
  });

  ignoreClick = evt => {
    evt.stopPropagation();
  };

  <template>
    {{#let (element this.tag) as |Tag|}}
      <Tag
        class={{this.classes}}
        data-test-spds-container={{this.tag}}
        id={{this.id}}
        style={{this.customProperties}}
        {{this.setupComponent}}
        {{this.setupDiscardClick}}
        {{this.validateArguments}}
        ...attributes
      >
        {{#if @content}}
          {{this.content}}
        {{else}}
          {{yield}}
        {{/if}}
      </Tag>
    {{/let}}
  </template>
}

export const CORE_CONTAINER_METADATA = SpdsCoreContainer.metadata;
